import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, themeQuartz, GridApi } from "ag-grid-community";
import { Constants, isPerClassTimeslotShow } from "@eagerdog/constants";
import { IEvent, IShow, QueryDto } from "@eagerdog/interfaces";
import { apiService } from "src/services";
import { localStorageService } from "src/services/local-storage.service";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Checkbox from "src/components/Checkbox/Checkbox";
import { processRunOrderData } from "../../../utils/runOrderDataProcessor";
import { webSocketService, ConnectionStatus } from "../../../services/websocket.service";
import { Config } from "@eagerdog/config";
import { convertTimeToMinutes } from "../../../components/EnterResults/StopWatch/StopWatch";
import { calculateMPH, calculatePoints, formatToTwoDecimals } from "../../../utils/fastCatCalculations";
import styles from "./RealtimeRunOrderTab.module.scss";

interface IProps {
  event: IEvent;
  shows: IShow[];
}

interface IGridRowData {
  id: string;
  classId: string;
  className: string;
  judgeName: string;
  armband: string;
  dogName: string;
  jumpHeight?: string;
  handlerName: string;
  breedName: string;
  scoreId: string;
  checkedIn: boolean;
  complete: boolean;
  scratched: boolean;
  conflict: boolean;
  // Show-specific fields
  time?: string;
  result?: string;
  score?: string;
  mph?: string;
  points?: string;
  // Time slot chosen at registration (General Obedience/Rally Show N Go)
  timeSlot?: string;
}

const RealtimeRunOrderTab: React.FC<IProps> = ({ event, shows }) => {
  const [selectedShow, setSelectedShow] = useState<IShow | null>(null);
  const [selectedRing, setSelectedRing] = useState<string>("");
  const [gridRowData, setGridRowData] = useState<IGridRowData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({
    connected: false,
    reconnecting: false,
    retryCount: 0,
    maxRetries: 5,
  });

  // Save current scroll position
  const saveScrollPosition = useCallback(() => {
    if (gridApiRef.current) {
      const scrollTop = gridApiRef.current.getVerticalPixelRange()?.top || 0;
      const scrollLeft = gridApiRef.current.getHorizontalPixelRange()?.left || 0;
      scrollPositionRef.current = { top: scrollTop, left: scrollLeft };
    }
  }, []);

  // Restore saved scroll position
  const restoreScrollPosition = useCallback(() => {
    if (gridApiRef.current && scrollPositionRef.current) {
      // Use setTimeout to ensure the grid has finished rendering
      setTimeout(() => {
        if (gridApiRef.current) {
          gridApiRef.current.ensureIndexVisible(
            Math.floor(scrollPositionRef.current.top / 45), // Assuming row height of 45px
            'top'
          );
        }
      }, 100);
    }
  }, []);

  // Grid ready callback to capture API reference
  const onGridReady = useCallback((params: any) => {
    gridApiRef.current = params.api;
  }, []);
  
  // Refs for component lifecycle management
  const isComponentMountedRef = useRef<boolean>(true);
  const unsubscribeFunctionsRef = useRef<(() => void)[]>([]);
  const selectedShowRef = useRef<IShow | null>(selectedShow);
  const selectedRingRef = useRef<string>(selectedRing);
  const gridApiRef = useRef<GridApi | null>(null);
  const scrollPositionRef = useRef<{ top: number; left: number }>({ top: 0, left: 0 });

  // Keep refs in sync with state
  useEffect(() => {
    selectedShowRef.current = selectedShow;
  }, [selectedShow]);

  useEffect(() => {
    selectedRingRef.current = selectedRing;
  }, [selectedRing]);

  // Load run order data with retry mechanism
  const loadRunOrderData = useCallback(async (forceRefresh = false, retryCount = 0) => {
    const currentShow = selectedShowRef.current;
    const currentRing = selectedRingRef.current;
    
    if (!currentShow || !currentRing) {
      return;
    }

    if (!forceRefresh && loading) {
      return;
    }

    // Save current scroll position before updating data
    if (forceRefresh && gridRowData.length > 0) {
      saveScrollPosition();
    }

    setLoading(true);
    setError(null);

    try {
      
      // Fetch fresh show data and owner scores in parallel
      // This ensures we get the latest completed_dogs data from the server
      const [freshShows, allScores] = await Promise.all([
        apiService.getClubEventShows(event.club_id, event._id, {
          sort: [{ attribute_name: "show_type", sort: "asc" }]
        }),
        apiService.getEventScoresReadOnly(event._id, {
          show_id: currentShow._id
        } as QueryDto)
      ]);

      // Find the fresh show data (this will have updated completed_dogs)
      const freshShow = freshShows.find((s: any) => s._id === currentShow._id);
      if (!freshShow) {
        throw new Error('Show not found in fresh show data');
      }

      // Use shared utility to process data consistently with admin RunOrderTab
      // IMPORTANT: Use freshShow instead of selectedShow to get updated completed_dogs
      const { processedClasses } = processRunOrderData({
        selectedShow: freshShow,
        selectedRing: currentRing,
        event: event, // Pass event for Fast CAT timeslot grouping
        allScores
      });



      // Convert to grid format using the same logic as RunOrderTab
      const gridData: IGridRowData[] = [];
      
      processedClasses.forEach(cls => {
        cls.dogs.forEach((dog, index) => {
          // Find the score data for this dog to get Fast CAT specific results
          const scoreData = allScores.find((score: any) => score._id === dog.scoreId);
          
          // Extract show-specific data if available
          let time = '';
          let result = '';
          let score = '';
          let jumpHeight = '';
          let mph = '';
          let points = '';
          
          // Extract jump height for obedience and rally
          if (currentShow.show_type === Constants.show_type.obedience && scoreData?.obedience_jump_height) {
            jumpHeight = scoreData.obedience_jump_height.toString();
          } else if (currentShow.show_type === Constants.show_type.rally_obedience && scoreData?.rally_jump_height) {
            jumpHeight = scoreData.rally_jump_height.toString();
          }
          
          if (currentShow.show_type === Constants.show_type.fast_cat && scoreData?.fastcat) {
            // Format time (already in seconds)
            if (scoreData.fastcat.time) {
              time = `${scoreData.fastcat.time.toFixed(2)}s`;
              
              // Calculate MPH and Points if we have time and height at withers
              if (scoreData.height_at_withers) {
                const calculatedMPH = calculateMPH(scoreData.fastcat.time);
                const calculatedPoints = calculatePoints(scoreData.fastcat.time, scoreData.height_at_withers);
                mph = formatToTwoDecimals(calculatedMPH);
                points = formatToTwoDecimals(calculatedPoints);
              }
            }
            
            // Format result
            if (scoreData.fastcat.result_type) {
              result = scoreData.fastcat.result_type === Constants.score_result_type.qualified ? 'Q' : 'NQ';
            }
          } else if (currentShow.show_type === Constants.show_type.obedience && scoreData?.obedience) {
            // Format result
            if (scoreData.obedience.result_type) {
              result = scoreData.obedience.result_type === Constants.score_result_type.qualified ? 'Q' : 'NQ';
            }
            
            // Calculate score (base 200 for obedience)
            if (scoreData.obedience.result_type === Constants.score_result_type.qualified) {
              let deductions = 0;
              if (scoreData.obedience.total_deductions_only) {
                deductions = scoreData.obedience.total_deductions ?? 0;
              } else {
                deductions = scoreData.obedience.element_deductions?.reduce((sum: number, item: any) => 
                  sum + parseInt(item.element_deductions ?? '0'), 0) ?? 0;
              }
              const finalScore = Math.max(200 - deductions, 0);
              score = finalScore.toString();
            } else {
              score = 'N/A';
            }
          } else if (currentShow.show_type === Constants.show_type.rally_obedience && scoreData?.rally_obedience) {
            // Format result
            if (scoreData.rally_obedience.result_type) {
              result = scoreData.rally_obedience.result_type === Constants.score_result_type.qualified ? 'Q' : 'NQ';
            }
            
            // Format time (convert seconds to mm:ss:tt format)
            if (scoreData.rally_obedience.time) {
              time = convertTimeToMinutes(scoreData.rally_obedience.time);
            }
            
            // Calculate score (base 100 for rally)
            if (scoreData.rally_obedience.result_type === Constants.score_result_type.qualified) {
              let deductions = 0;
              if (scoreData.rally_obedience.total_deductions_only) {
                deductions = scoreData.rally_obedience.total_deductions ?? 0;
              } else {
                deductions = scoreData.rally_obedience.element_deductions?.reduce((sum: number, item: any) => 
                  sum + parseInt(item.element_deductions ?? '0'), 0) ?? 0;
              }
              const finalScore = Math.max(100 - deductions, 0);
              score = finalScore.toString();
            } else {
              score = 'N/A';
            }
          }

          // Fallback: use score.status for result when no sub-score result has been entered
          if (!result && scoreData) {
            if (scoreData.status === Constants.score_status.scratched) {
              result = 'Scratched';
            } else if (scoreData.status === Constants.score_status.conflict || (scoreData as any).conflict === true) {
              result = 'Conflict';
            }
          }
          
          // Construct className based on show type
          let className: string;
          
          if (event.sanctioning_club === Constants.sanctioning_club.AKC && 
              (freshShow.show_type === Constants.show_type.obedience || 
               freshShow.show_type === Constants.show_type.rally_obedience)) {
            // For AKC Obedience/Rally Obedience, omit the element (always "Obedience" or "Rally Obedience")
            className = `${cls.level} - Judge: ${cls.judgeName}`;
          } else {
            className = `${cls.showElement} - ${cls.level} - Judge: ${cls.judgeName}`;
          }
          
          gridData.push({
            id: `${cls.id}-${dog.scoreId}`,
            classId: cls.id,
            className,
            judgeName: `Judge: ${cls.judgeName}`,
            armband: dog.armband,
            dogName: dog.dogName,
            jumpHeight,
            timeSlot: dog.timeSlot,
            handlerName: dog.handlerName,
            breedName: dog.breedName,
            scoreId: dog.scoreId,
            checkedIn: scoreData?.checked_in || false,
            complete: scoreData?.status === 'complete' || scoreData?.complete === true,
            scratched: scoreData?.status === 'scratched',
            conflict: scoreData?.status === 'conflict' || !!(scoreData as any)?.conflict,
            time,
            result,
            score,
            mph,
            points
          });
        });
      });
      
      if (gridData.length === 0) {
        // Show a helpful message when no data is available
        gridData.push({
          id: 'no-data',
          classId: '',
          className: 'No Run Order Available',
          judgeName: '',
          armband: 'N/A',
          dogName: 'No run order has been set up for this show/ring',
          jumpHeight: '',
          handlerName: 'Please contact the event organizers',
          breedName: 'N/A',
          scoreId: '',
          checkedIn: false,
          complete: false,
          scratched: false,
          conflict: false,
          time: '',
          result: '',
          score: ''
        });
      }


      setGridRowData(gridData);
      setLastRefreshTime(new Date());

      // Restore scroll position after data update (only for force refresh/webhook updates)
      if (forceRefresh && gridRowData.length > 0) {
        restoreScrollPosition();
      }

    } catch (err: any) {
      // Error loading run order data
      
      // Retry logic for 401 errors (session expired)
      if (err?.response?.status === 401 && retryCount < 2) {

        setLoading(false);
        // Wait a bit for token refresh to complete, then retry
        setTimeout(() => {
          loadRunOrderData(forceRefresh, retryCount + 1);
        }, 1000);
        return;
      }
      
      // Check if it's a permission error (400 or 403)
      if (err?.response?.status === 400 || err?.response?.status === 403) {
        setError('You need to be registered for this event to view run order data. Please register for the event to access real-time run order updates.');
      } else if (err?.response?.status === 401) {
        setError('Your session has expired. Please refresh the page and log in again to view run order data.');
      } else {
        setError(`Failed to load run order data. Please try refreshing the page or contact support if the problem persists.`);
      }
    } finally {
      setLoading(false);
    }
  }, [event, loading, gridRowData.length, restoreScrollPosition, saveScrollPosition]);

  // Manual refresh function
  const handleManualRefresh = useCallback(() => {
    loadRunOrderData(true);
  }, [loadRunOrderData]);

  // WebSocket connection management
  const connectWebSocket = useCallback(async () => {
    try {
      // Disconnect any existing WebSocket connection first
      webSocketService.disconnect();
      
      // Try to get a fresh token by making a test API call
      // This will trigger token refresh if needed
      let token = localStorageService.getItem(localStorageService.ACCESS_TOKEN);
      
      // Make a simple API call to trigger token refresh if needed
      try {
        await apiService.getClubEventShows(event.club_id, event._id, {
          $limit: 1 // Just get 1 record to test authentication
        });
        
        // Get the token again in case it was refreshed by the HTTP interceptor
        const refreshedToken = localStorageService.getItem(localStorageService.ACCESS_TOKEN);
        if (refreshedToken !== token) {
          token = refreshedToken;
        }
      } catch (apiError) {
        // Try to get token anyway
        token = localStorageService.getItem(localStorageService.ACCESS_TOKEN);
      }
      
      if (!token) {
        setError('Authentication required for real-time updates');
        return;
      }

      // Connect to WebSocket with authentication
      await webSocketService.connect({
        url: Config.web.REACT_APP_API_URL || 'http://localhost:3001',
        namespace: '/run-order',
        token,
      });

    } catch (error) {
      setError('Authentication failed. Please refresh the page and try again.');
    }

    // Setup event listeners with cleanup tracking
    const unsubscribeConnectionStatus = webSocketService.onConnectionStatus((status) => {
      if (isComponentMountedRef.current) {
        setConnectionStatus(status);
        
        if (status.connected) {
          setError(null);
          // Join room when connected and show/ring are selected
          if (selectedShow && selectedRing) {
            joinRunOrderRoom();
          }
        } else if (status.error) {
          setError(status.error);
        }
      }
    });

    const unsubscribeRunOrderUpdate = webSocketService.onRunOrderUpdate((data) => {
      if (isComponentMountedRef.current) {
        // Handle updates based on current selection state (using refs to avoid stale closure)
        const currentShow = selectedShowRef.current;
        const currentRing = selectedRingRef.current;
        
        // If no show/ring is selected, auto-select from the first update
        if (!currentShow || !currentRing) {
          // Find the show that matches the update
          const matchingShow = availableShows.find(show => show._id === data.showId);
          if (matchingShow && !currentShow) {
            setSelectedShow(matchingShow);
          }
          
          if (!currentRing && data.ring) {
            setSelectedRing(data.ring);
          }
          
          // Don't call loadRunOrderData here - let the useEffect handle it when state updates
          setLastRefreshTime(new Date());
        } else if (data.showId === currentShow._id && data.ring === currentRing) {
          // Simple approach: Always reload data from database when we get an update
          // This ensures perfect synchronization regardless of what changed
          loadRunOrderData(true);
          setLastRefreshTime(new Date());
        }
      }
    });

    const unsubscribeError = webSocketService.onError((error) => {
      if (isComponentMountedRef.current) {

        setError(error);
      }
    });

    // Store unsubscribe functions for cleanup
    unsubscribeFunctionsRef.current = [
      unsubscribeConnectionStatus,
      unsubscribeRunOrderUpdate,
      unsubscribeError,
    ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShow, selectedRing, loadRunOrderData]);

  const joinRunOrderRoom = useCallback(() => {
    if (selectedShow && selectedRing && webSocketService.isConnected) {
      webSocketService.joinRunOrderRoom({
        eventId: event._id,
        showId: selectedShow._id,
        ring: selectedRing,
      });
    }
  }, [selectedShow, selectedRing, event._id]);

  const disconnectWebSocket = useCallback(() => {

    
    // Clean up subscriptions
    unsubscribeFunctionsRef.current.forEach(unsubscribe => unsubscribe());
    unsubscribeFunctionsRef.current = [];
    
    // Disconnect WebSocket
    webSocketService.disconnect();
    
    // Reset connection status
    setConnectionStatus({
      connected: false,
      reconnecting: false,
      retryCount: 0,
      maxRetries: 5,
    });
  }, []);

  // Retry connection (called by user action)
  const retryConnection = useCallback(() => {

    webSocketService.retryConnection();
  }, []);

  // Initialize WebSocket connection on mount
  useEffect(() => {
    isComponentMountedRef.current = true;
    connectWebSocket();
    
    return () => {
      isComponentMountedRef.current = false;
      disconnectWebSocket();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount/unmount

  // Load data and join room when show/ring changes
  useEffect(() => {
    if (selectedShow && selectedRing) {
      loadRunOrderData();
      // Join WebSocket room if connected
      if (webSocketService.isConnected) {
        joinRunOrderRoom();
      }
    } else {
      setGridRowData([]);
      // Leave current room when no show/ring selected
      webSocketService.leaveRunOrderRoom();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShow, selectedRing]); // Remove function dependencies to prevent infinite loops

  // Join room when WebSocket connects and show/ring are already selected
  useEffect(() => {
    if (connectionStatus.connected && selectedShow && selectedRing) {
      joinRunOrderRoom();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionStatus.connected, selectedShow, selectedRing]);

  // Filter shows that have real-time run order enabled

  // AG-Grid theme - customized with orange colors (read-only version)
  const theme = themeQuartz.withParams({
    fontFamily: "Roboto",
    headerFontFamily: "Roboto",
    cellFontFamily: "Roboto",
    accentColor: "#ff6633",
    selectedRowBackgroundColor: "rgba(255, 102, 51, 0.1)",
    rangeSelectionBorderColor: "#ff6633"
  });

  // AG-Grid column definitions (read-only version of admin)
  const columnDefs: ColDef[] = useMemo(() => {
    const resultCellRenderer = (params: any) => {
      const result = params.data.result;
      if (!result) return <span></span>;
      let color = '#dc3545';
      if (result === 'Q') color = '#28a745';
      else if (result === 'Scratched') color = '#9a3412';
      else if (result === 'Conflict') color = '#92400e';
      return <span style={{ fontWeight: 'bold', color }}>{result}</span>;
    };

    // Show-specific columns inserted right after Armband
    const showSpecificColumns: ColDef[] = [];
    if (selectedShow?.show_type === Constants.show_type.fast_cat) {
      showSpecificColumns.push(
        { headerName: "Result", field: "result", width: 120, minWidth: 120, flex: 0, cellRenderer: resultCellRenderer, cellStyle: { textAlign: 'center' } as any },
        { headerName: "Time",   field: "time",   width: 120, minWidth: 120, flex: 0 },
        { headerName: "MPH",    field: "mph",    width: 120, minWidth: 120, flex: 0 },
        { headerName: "Points", field: "points", width: 120, minWidth: 120, flex: 0 }
      );
    } else if (selectedShow?.show_type === Constants.show_type.obedience) {
      showSpecificColumns.push(
        { headerName: "Result", field: "result", width: 120, minWidth: 120, flex: 0, cellRenderer: resultCellRenderer, cellStyle: { textAlign: 'center' } as any },
        { headerName: "Score",  field: "score",  width: 120, minWidth: 120, flex: 0 }
      );
    } else if (selectedShow?.show_type === Constants.show_type.rally_obedience) {
      showSpecificColumns.push(
        { headerName: "Result", field: "result", width: 120, minWidth: 120, flex: 0, cellRenderer: resultCellRenderer, cellStyle: { textAlign: 'center' } as any },
        { headerName: "Time",   field: "time",   width: 120, minWidth: 120, flex: 0 },
        { headerName: "Score",  field: "score",  width: 120, minWidth: 120, flex: 0 }
      );
    }

    return [
      {
        headerName: "Class / Judge",
        field: "className",
        rowGroup: true,
        hide: true
      },
      {
        headerName: "Checked-In",
        field: "checkedIn",
        width: 135,
        minWidth: 135,
        flex: 0,
        cellRenderer: (params: any) => (
          <div className={params.data.checkedIn ? styles.checkedInCheckbox : styles.uncheckedCheckbox}>
            <Checkbox
              id={`checkin-checkbox-${params.data.scoreId}`}
              label=""
              value={params.data.checkedIn || false}
              disabled={true}
              onChange={() => {}}
            />
          </div>
        ),
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' } as any
      },
      {
        headerName: "Complete",
        field: "complete",
        width: 135,
        minWidth: 135,
        flex: 0,
        cellRenderer: (params: any) => (
          <div className={styles.completedCheckboxContainer}>
            <Checkbox
              id={`complete-checkbox-${params.data.scoreId}`}
              label=""
              value={params.data.complete || false}
              disabled={true}
              onChange={() => {}}
            />
          </div>
        ),
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' } as any
      },
      {
        headerName: "Status",
        field: "scratched",
        width: 130,
        minWidth: 130,
        flex: 0,
        cellRenderer: (params: any) => {
          const { scratched, conflict } = params.data || {};
          if (scratched) {
            return (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '2px 10px',
                height: '22px',
                borderRadius: '999px',
                fontSize: '12px',
                lineHeight: 1,
                fontWeight: 700,
                background: '#ffedd5',
                color: '#9a3412',
                border: '1px solid #9a3412',
              }}>
                Scratched
              </span>
            );
          }
          if (conflict) {
            return (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '2px 10px',
                height: '22px',
                borderRadius: '999px',
                fontSize: '12px',
                lineHeight: 1,
                fontWeight: 700,
                background: '#fef3c7',
                color: '#b45309',
                border: '1px solid #b45309',
              }}>
                Conflict
              </span>
            );
          }
          return <span style={{ color: '#bbb' }}>—</span>;
        },
        cellStyle: { display: 'flex', alignItems: 'center' } as any,
      },
      {
        headerName: "Time Slot",
        field: "timeSlot",
        width: 180,
        minWidth: 180,
        flex: 0,
        cellStyle: { fontWeight: 'bold' } as any,
        hide: !isPerClassTimeslotShow(event.sanctioning_club, selectedShow?.show_type)
      },
      {
        headerName: "Armband",
        field: "armband",
        width: 130,
        minWidth: 130,
        flex: 0,
        cellStyle: { fontWeight: 'bold', color: '#ff6633' } as any,
        hide: selectedShow?.show_type === Constants.show_type.fast_cat
      },
      // Result / Time / Score immediately after Armband
      ...showSpecificColumns,
      {
        headerName: "Dog",
        field: "dogName",
        flex: 1,
        minWidth: 150
      },
      {
        headerName: "Jump Height",
        field: "jumpHeight",
        width: 115,
        minWidth: 115,
        flex: 0,
        hide: selectedShow?.show_type !== Constants.show_type.obedience &&
              selectedShow?.show_type !== Constants.show_type.rally_obedience
      },
      {
        headerName: "Breed",
        field: "breedName",
        flex: 1,
        minWidth: 180
      },
      {
        headerName: "Owner",
        field: "handlerName",
        flex: 1,
        minWidth: 180,
        cellRenderer: (params: any) => {
          const fullName = params.data.handlerName;
          if (!fullName) return '';
          const nameParts = fullName.trim().split(' ');
          if (nameParts.length < 2) return fullName;
          return `${nameParts[0].charAt(0).toUpperCase()}. ${nameParts[nameParts.length - 1]}`;
        }
      }
    ];
  }, [selectedShow?.show_type, event.sanctioning_club]);

  // Calculate progress - using 'complete' field instead of 'completed'
  const completedCount = gridRowData.filter(row => row.complete).length;
  const totalCount = gridRowData.length;

  const availableShows = useMemo(() => {
    // Use the same supported show types as Event.tsx
    const runorderSupportedShowTypes = [
      Constants.show_type.conformation,               // "Conformation"
      Constants.show_type.country_rat_race,           // "Country Rat Race"
      Constants.show_type.fast_cat,                   // "Fast CAT"
      Constants.show_type.fetch,                      // "Fetch"
      Constants.show_type.nosework,                   // "Nosework"
      Constants.show_type.obedience,                  // "Obedience"
      Constants.show_type.rally_obedience,            // "Rally Obedience"
      Constants.show_type.scentwork,                  // "Scentwork"
      Constants.show_type.shed_dog,                   // "Shed Dog"
      Constants.show_type.water_dog,                  // "Water Dog"
      Constants.show_type.precision_coursing          // "Precision Coursing"
    ];
    
    return shows.filter(show => 
      show.show_run_order === true && 
      runorderSupportedShowTypes.includes(show.show_type) &&
      show.rings && show.rings.length > 0
    );
  }, [shows]);

  // Create show options for Dropdown component
  const showOptions = useMemo(() => {
    return availableShows.map(show => ({
      id: show._id,
      value: `${show.show_type} - ${show.show_name}`,
      data: show
    }));
  }, [availableShows]);

  // Create ring options for Dropdown component
  const ringOptions = useMemo(() => {
    if (!selectedShow?.rings || selectedShow.rings.length === 0) {
      // If no show is selected, show all possible rings from all shows
      const allRings = new Set<string>();
      availableShows.forEach(show => {
        show.rings?.forEach(ring => {
          allRings.add(ring.ring);
        });
      });
      return Array.from(allRings).map((ringName, index) => ({
        id: `ring${index}`,
        value: ringName,
        data: null
      }));
    }
    return selectedShow.rings.map((ring, index) => ({
      id: `ring${index}`,
      value: ring.ring,
      data: ring
    }));
  }, [selectedShow?.rings, availableShows]);

  // Handle show selection change
  const handleShowChange = useCallback((e: any, value: IOption) => {
    const show = availableShows.find(s => s._id === value.id);
    setSelectedShow(show || null);
    setSelectedRing('');
  }, [availableShows]);

  // Handle ring selection change
  const handleRingChange = useCallback((e: any, value: IOption) => {
    setSelectedRing(value.value);
  }, []);



  if (availableShows.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
        <h3>No Real-time Run Orders Available</h3>
        <p>Real-time run order viewing is not enabled for any shows in this event.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '10px 8px 20px 8px', height: '100%', width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      <div style={{ marginBottom: '20px' }}>
        <p style={{ color: '#666', marginBottom: '20px', marginTop: '0' }}>
          View live run order progress for this event. This updates automatically when judges mark dogs as complete.
        </p>
        
        {/* Show and Ring selectors on same line */}
        <div style={{ 
          display: 'flex', 
          gap: '20px', 
          marginBottom: '20px',
          flexWrap: 'wrap',
          alignItems: 'end'
        }}>
          <div style={{ minWidth: '200px', flex: '1 1 200px' }}>
            <Dropdown
              label="Select Show"
              placeholder="Select a show..."
              options={showOptions}
              value={showOptions.find(option => option.id === selectedShow?._id) || { id: '', value: '', data: null }}
              onChange={handleShowChange}
            />
        </div>

          <div style={{ minWidth: '200px', flex: '1 1 200px' }}>
            <Dropdown
              label="Select Ring"
              placeholder="Select a ring..."
              options={ringOptions}
              value={ringOptions.find(option => option.value === selectedRing) || { id: '', value: '', data: null }}
              onChange={handleRingChange}
            />
          </div>
        </div>
      </div>


      {selectedShow && selectedRing ? (
        <div style={{ width: '100%', maxWidth: '100%' }}>
          {/* Progress and Connection Status */}
        <div style={{ 
            marginBottom: '20px',
            padding: '10px 15px',
          backgroundColor: '#f8f9fa', 
          borderRadius: '8px',
          border: '1px solid #e9ecef'
        }}>
            {/* Connection Status */}
            <div style={{ margin: '0', fontSize: '12px' }}>
              <span style={{ 
                color: connectionStatus.connected ? '#28a745' : connectionStatus.reconnecting ? '#ffc107' : '#dc3545',
                fontWeight: '500',
                marginRight: '8px'
              }}>
                ●
              </span>
              <span style={{ color: '#666', marginRight: '8px' }}>
                {connectionStatus.connected 
                  ? 'Connected - Real-time updates active'
                  : connectionStatus.reconnecting 
                    ? `Reconnecting... (${connectionStatus.retryCount}/${connectionStatus.maxRetries})`
                    : 'Disconnected'
                }
              </span>
              
              {!connectionStatus.connected && connectionStatus.retryCount >= connectionStatus.maxRetries && (
                <span
                  onClick={retryConnection}
                  style={{
                    color: '#ff6633',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontWeight: '500',
                    marginLeft: '8px'
                  }}
                >
                  Try Again
                </span>
              )}
            </div>
            
            {lastRefreshTime && (
              <p style={{ margin: '4px 0 0 0', color: '#888', fontSize: '12px' }}>
                Last updated: {lastRefreshTime.toLocaleString()}
                {connectionStatus.connected && (
                  <span style={{ marginLeft: '8px', color: '#666' }}>
                    (Auto-updating)
                  </span>
                )}
                {!connectionStatus.connected && (
                  <span
                    onClick={loading ? undefined : handleManualRefresh}
                    style={{
                      color: loading ? '#ccc' : '#ff6633',
                      textDecoration: 'underline',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontWeight: '500',
                      marginLeft: '8px'
                    }}
                  >
                    {loading ? 'Refreshing...' : 'Manual Refresh'}
                  </span>
                )}
              </p>
            )}
          </div>

          {/* Connection Error Banner */}
          {!connectionStatus.connected && connectionStatus.retryCount >= connectionStatus.maxRetries && (
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#fff3cd', 
              color: '#856404',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #ffeaa7'
            }}>
              <strong>You're currently not connected.</strong> Progress will be updated once you are connected again.{' '}
              <span
                onClick={retryConnection}
                style={{
                  color: '#ff6633',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Try Again
              </span>
            </div>
          )}

          {/* General Error Display */}
          {error && (
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#f8d7da', 
              color: '#721c24',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #f5c6cb'
            }}>
              {error}
            </div>
          )}

          {/* Progress and Legend */}
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            flexWrap: 'wrap',
            padding: '16px 0',
            borderTop: '1px solid #e9ecef',
            marginTop: '16px'
          }}>
            <div style={{
              fontSize: '14px',
              color: '#6c757d',
              fontWeight: '500',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <div>
                Progress: {gridRowData.filter(row => row.checkedIn).length} checked in, {completedCount} completed of {totalCount} runs
                {completedCount === totalCount && totalCount > 0 ? (
                  <span style={{ 
                    color: '#22c55e',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    background: 'rgba(34, 197, 94, 0.1)',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: '8px'
                  }}>✓</span>
                ) : null}
              </div>
              <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#6c757d' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ color: '#ff8c00', fontWeight: 'bold', fontSize: '14px' }}>✓</span> Checked In
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ color: '#28a745', fontWeight: 'bold', fontSize: '14px' }}>✓</span> Complete
                </span>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div style={{ 
              padding: '40px', 
              textAlign: 'center',
              color: '#666'
            }}>
              <p>Loading run order data...</p>
            </div>
          )}

          {/* Run Order Grid */}
          {!loading && gridRowData.length > 0 && (
            <div style={{ 
              height: '600px', 
              width: '100%',
              marginTop: '20px'
            }}>
              <AgGridReact
                theme={theme}
                rowData={gridRowData}
                columnDefs={columnDefs}
                onGridReady={onGridReady}
                defaultColDef={{
                  resizable: true,
                  sortable: false,
                  filter: false
                }}
                suppressRowClickSelection={true}
                animateRows={true}
                getRowId={(params) => params.data.id}
                suppressMovableColumns={true}
                rowHeight={45}
                headerHeight={45}
                groupDisplayType="groupRows"
                groupDefaultExpanded={-1}
                suppressAggFuncInHeader={true}
                autoGroupColumnDef={{
                  headerName: "Class / Judge",
                  minWidth: 400,
                  flex: 2,
                  cellRendererParams: {
                    suppressCount: true,
                    suppressDoubleClickExpand: false,
                    suppressEnterExpand: false
                  }
                }}
              />
            </div>
          )}

          {/* No Data State */}
          {!loading && gridRowData.length === 0 && !error && (
            <div style={{ 
              padding: '40px', 
              textAlign: 'center',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              color: '#666'
            }}>
              <p>No run order data available for this show and ring.</p>
            </div>
          )}

        </div>
      ) : (
        <div style={{ 
          padding: '40px', 
          textAlign: 'center', 
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          color: '#666'
        }}>
          <p>Please select a show and ring to view the run order.</p>
        </div>
      )}
    </div>
  );
};

export default RealtimeRunOrderTab;